import React from 'react';
import {
  Activity,
  Zap,
  HardDrive,
  Compass,
  Cpu,
  Pause,
  Play,
  XOctagon,
  Image as ImageIcon,
  Check,
} from 'lucide-react';
import { IngestionProgressStats } from '../../types/ingestion';

export interface IngestionProgressProps {
  stats: IngestionProgressStats;
  isPaused: boolean;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s < 10 ? '0' : ''}${s}s`;
}

export const IngestionProgress: React.FC<IngestionProgressProps> = ({
  stats,
  isPaused,
  onPause,
  onResume,
  onCancel,
}) => {
  const {
    totalCount,
    processedCount,
    progressPercent,
    photosPerSecond,
    estimatedRemainingSeconds,
    elapsedSeconds,
    activeWorkers,
    maxWorkers,
    currentFileName,
    currentCategory,
    storage,
    matching,
    recentThumbnails,
  } = stats;

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col gap-6 p-4 sm:p-6 bg-slate-900/95 border border-slate-800 rounded-2xl shadow-2xl backdrop-blur-md">
      {/* Header Status Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/30 flex items-center justify-center text-brand-400">
            <Activity className={`w-5 h-5 ${isPaused ? 'opacity-50' : 'animate-pulse'}`} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-100 flex items-center gap-2">
              <span>{isPaused ? 'Elaborazione in Pausa' : 'Elaborazione Foto in Corso...'}</span>
              <span className="text-xs font-mono font-medium px-2 py-0.5 rounded-full bg-brand-500/20 text-brand-300 border border-brand-500/30">
                {progressPercent.toFixed(0)}%
              </span>
            </h2>
            <p className="text-xs text-slate-400 truncate max-w-sm">
              {currentFileName ? (
                <>
                  <span className="text-brand-400 font-medium">[{currentCategory || 'Generale'}]</span>{' '}
                  {currentFileName}
                </>
              ) : (
                'Inizializzazione pool di compressione...'
              )}
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {isPaused ? (
            <button
              type="button"
              onClick={onResume}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium transition shadow-sm shadow-emerald-600/30 cursor-pointer"
            >
              <Play className="w-3.5 h-3.5" />
              Riprendi
            </button>
          ) : (
            <button
              type="button"
              onClick={onPause}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-xs font-medium transition cursor-pointer"
            >
              <Pause className="w-3.5 h-3.5" />
              Pausa
            </button>
          )}

          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-950/40 hover:bg-red-900/60 text-red-300 border border-red-800/60 rounded-lg text-xs font-medium transition cursor-pointer"
          >
            <XOctagon className="w-3.5 h-3.5" />
            Annulla
          </button>
        </div>
      </div>

      {/* Main Dual-Tier Progress Bar */}
      <div className="flex flex-col gap-1.5">
        <div className="flex justify-between items-center text-xs font-medium">
          <span className="text-slate-300">
            Avanzamento Globale: <span className="font-bold text-slate-100">{processedCount}</span> di{' '}
            <span className="text-slate-400">{totalCount} foto</span>
          </span>
          <span className="text-brand-400 font-mono">{progressPercent.toFixed(1)}%</span>
        </div>
        <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden p-0.5 border border-slate-700/60 relative">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-500 via-sky-400 to-emerald-400 transition-all duration-200 shadow-lg shadow-brand-500/20"
            style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
          />
        </div>
      </div>

      {/* 4 Telemetry Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Card 1: Throughput & ETA */}
        <div className="bg-slate-800/60 border border-slate-700/70 rounded-xl p-3.5 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 mt-0.5">
            <Zap className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-400">Velocità & Tempo</div>
            <div className="text-base font-bold text-slate-100 mt-0.5">
              {photosPerSecond.toFixed(1)} <span className="text-xs font-normal text-slate-400">foto/sec</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
              <span>Trascorsi: {formatDuration(elapsedSeconds)}</span>
              <span className="text-amber-300/90 font-medium">
                ETA: ~{formatDuration(estimatedRemainingSeconds)}
              </span>
            </div>
          </div>
        </div>

        {/* Card 2: Storage & Compression */}
        <div className="bg-slate-800/60 border border-slate-700/70 rounded-xl p-3.5 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20 mt-0.5">
            <HardDrive className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-400">Spazio & Compressione</div>
            <div className="text-base font-bold text-slate-100 mt-0.5 flex items-center gap-1.5">
              <span>{formatBytes(storage.totalCompressedBytes)}</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                -{storage.compressionRatioPercent}%
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-1 truncate">
              Thumb: ~{storage.averageThumbnailKb.toFixed(0)} KB • HD: ~{storage.averagePreviewKb.toFixed(0)} KB
            </div>
          </div>
        </div>

        {/* Card 3: GPS Matching */}
        <div className="bg-slate-800/60 border border-slate-700/70 rounded-xl p-3.5 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mt-0.5">
            <Compass className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-400">Geolocalizzazione Polarsteps</div>
            <div className="text-base font-bold text-slate-100 mt-0.5">
              {matching.matchRatePercentage.toFixed(1)}%{' '}
              <span className="text-xs font-normal text-slate-400">correlate</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
              <span className="text-emerald-400 font-medium">{matching.totalGeotagged} su mappa</span>
              <span className="text-slate-500">•</span>
              <span className="text-slate-400">{matching.unmatched} non geocod.</span>
            </div>
          </div>
        </div>

        {/* Card 4: Worker Pool Health */}
        <div className="bg-slate-800/60 border border-slate-700/70 rounded-xl p-3.5 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 mt-0.5">
            <Cpu className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-400">Motore Multi-Thread</div>
            <div className="text-base font-bold text-slate-100 mt-0.5">
              {activeWorkers} / {maxWorkers}{' '}
              <span className="text-xs font-normal text-slate-400">Worker WebP</span>
            </div>
            <div className="text-[11px] text-emerald-400/90 mt-1 flex items-center gap-1 font-medium">
              <Check className="w-3 h-3" />
              <span>Storage ({storage.storageEngineType})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Live Sliding-Window Thumbnail Stream */}
      {recentThumbnails.length > 0 && (
        <div className="flex flex-col gap-2 pt-2 border-t border-slate-800/80">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider">
            <span className="flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5 text-brand-400" />
              Flusso Anteprime in Tempo Reale
            </span>
            <span className="text-[11px] text-slate-500 font-mono">ultimi {recentThumbnails.length} fotogrammi</span>
          </div>

          <div className="grid grid-cols-6 gap-2 overflow-x-auto pb-1">
            {recentThumbnails.map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square rounded-lg overflow-hidden border border-slate-700/80 bg-slate-800 shadow-sm"
              >
                <img
                  src={item.objectUrl}
                  alt={item.fileName}
                  className="w-full h-full object-cover transition duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent flex flex-col justify-end p-1">
                  <span className="text-[9px] text-slate-200 font-medium truncate leading-tight">
                    {item.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
